import React, { useEffect, useState } from "react";
import { IFilterReport } from "../../../../../types/filter.type";
import moment from "moment";
import FilterContainer from "../../FilterContainer";
import { Stack } from "@mui/material";
import FilterTimeFromTo from "../../FilterTimeFromTo";
import { ConcernType } from "../../../../../types/report.type";
import FilterRadios from "../../FilterRadios";
import { formatDateDisplay, groupBy } from "../../../../../utils/helpers";
import { cloneDeep } from "lodash";

type ModeType = "public" | "private" | "friends" | "anonymous";
const modes: Record<ModeType, string> = {
  public: "Công khai",
  private: "Chỉ mình tôi",
  friends: "Bạn bè",
  anonymous: "Ẩn danh",
};

const types = [
  { label: "Feed", value: "feed" },
  { label: "Forum", value: "forum" },
];

const concerns: ConcernType[] = [
  {
    value: "thoigian",
    label: "Thời gian",
  },
  {
    value: "mode",
    label: "Chế độ",
    title: "Thống kê bài đăng theo chế độ",
    api: "fos_rpposts",
    showChart: true,
    chartLabel: "Chế độ",
    generateChartLabels: (data) => {
      const clonedData = cloneDeep(data);
      clonedData.shift();
      return clonedData.map((item) => item.name);
    },
    generateChartValues: (data) => {
      const clonedData = cloneDeep(data);
      clonedData.shift();
      return clonedData.map((item) => item.sl);
    },
    convertData: (data) => {
      const groupedData: any[] = groupBy({
        data,
        callbackMatch: (item) => item.mode,
      }) as any[];
      const result: { name: string; sl: number }[] = [];
      let totalPost = 0;
      for (let i = 0; i < groupedData.length; i++) {
        const item = groupedData[i];
        result.push({
          name: modes[item[0].mode as ModeType],
          sl: item?.length || 0,
        });
        totalPost += item?.length || 0;
      }
      result.unshift({
        name: "Tổng cộng",
        sl: totalPost,
      });
      return result;
    },
    columns: [
      {
        name: "Chế độ",
        width: "130px",
        wrap: true,
        selector: (row) => row.name,
      },
      {
        name: "Số lượng",
        width: "120px",
        right: true,
        selector: (row) => row.sl,
      },
    ],
  },
];

type TimeType =
  | "fromTo"
  | "today"
  | "yesterday"
  | "thisweek"
  | "lastweek"
  | "7daysago"
  | "thismonth"
  | "lastmonth"
  | "30daysago"
  | "thisquarter"
  | "lastquarter"
  | "thisyear"
  | "lastyear";

const timeConcerns: Record<TimeType, ConcernType> = {
  fromTo: {
    label: "From to",
    value: "fromTo",
    title: "Thống kê bài đăng theo thời gian",
    api: "fos_rpposts",
    convertData: (data) => {
      const result: { ngay: string; sl: number }[] = [];
      result.unshift({
        ngay:
          data?.length > 0
            ? `${formatDateDisplay(
                data[0]?.date_created,
                "DD/MM/YYYY"
              )} - ${formatDateDisplay(
                data[data.length - 1]?.date_created,
                "DD/MM/YYYY"
              )}`
            : "Tổng cộng",
        sl: data?.length || 0,
      });
      return result;
    },
    columns: [
      {
        name: "Ngày",
        width: "200px",
        wrap: true,
        selector: (row) => row.ngay,
      },
      {
        name: "Số lượng",
        width: "150px",
        right: true,
        selector: (row) => row.sl,
      },
    ],
  },
  today: {
    label: "Today",
    value: "today",
    title: "Bài đăng trong hôm nay",
    api: "fos_rpposts",
    convertData: (data) => {
      const result: { ngay: string; sl: number }[] = [];
      const groupedData: any[] = groupBy({
        data,
        callbackMatch: (item) => {
          const day = moment(item.date_created).format("DD/MM/YYYY");
          return day;
        },
      }) as any[];
      let total = 0;
      for (let i = 0; i < groupedData.length; i++) {
        const item = groupedData[i];
        result.push({
          ngay: moment(item[0].date_created).format("DD/MM/YYYY"),
          sl: item.length || 0,
        });
        total += item.length || 0;
      }
      result.unshift({
        ngay: "Tổng cộng",
        sl: total,
      });
      return result;
    },
    columns: [
      {
        name: "Ngày",
        width: "130px",
        wrap: true,
        selector: (row) => row.ngay,
      },
      {
        name: "Số lượng",
        width: "150px",
        right: true,
        selector: (row) => row.sl,
      },
    ],
  },
  yesterday: {
    label: "Yesterday",
    value: "yesterday",
    title: "Bài đăng hôm qua",
    api: "fos_rpposts",
    convertData: (data) => {
      const result: { ngay: string; sl: number }[] = [];
      const groupedData: any[] = groupBy({
        data,
        callbackMatch: (item) => {
          const day = moment(item.date_created).format("DD/MM/YYYY");
          return day;
        },
      }) as any[];
      let total = 0;
      for (let i = 0; i < groupedData.length; i++) {
        const item = groupedData[i];
        result.push({
          ngay: moment(item[0].date_created).format("DD/MM/YYYY"),
          sl: item.length || 0,
        });
        total += item.length || 0;
      }
      result.unshift({
        ngay: "Tổng cộng",
        sl: total,
      });
      return result;
    },
    columns: [
      {
        name: "Ngày",
        width: "130px",
        wrap: true,
        selector: (row) => row.ngay,
      },
      {
        name: "Số lượng",
        width: "150px",
        right: true,
        selector: (row) => row.sl,
      },
    ],
  },
  thisweek: {
    label: "Thisweek",
    value: "thisweek",
    title: "Thống kê bài đăng tuần này",
    api: "fos_rpposts",
    showChart: true,
    chartLabel: "Ngày",
    generateChartLabels: (data) => {
      const clonedData = cloneDeep(data);
      clonedData.shift();
      return clonedData.map((item) => item.ngay);
    },
    generateChartValues: (data) => {
      const clonedData = cloneDeep(data);
      clonedData.shift();
      return clonedData.map((item) => item.sl);
    },
    convertData: (data) => {
      const result: { ngay: string; sl: number }[] = [];
      const groupedData: any[] = groupBy({
        data,
        callbackMatch: (item) => {
          const day = moment(item.date_created).format("DD/MM/YYYY");
          return day;
        },
      }) as any[];
      let total = 0;
      for (let i = 0; i < groupedData.length; i++) {
        const item = groupedData[i];
        result.push({
          ngay: formatDateDisplay(item[0].date_created),
          sl: item?.length || 0,
        });
        total += item.length || 0;
      }
      result.unshift({
        ngay: "Tổng cộng",
        sl: total,
      });
      return result;
    },
    columns: [
      {
        name: "Ngày",
        width: "130px",
        wrap: true,
        selector: (row) => row.ngay,
      },
      {
        name: "Số lượng",
        width: "150px",
        right: true,
        selector: (row) => row.sl,
      },
    ],
  },
  lastweek: {
    label: "Lastweek",
    value: "lastweek",
    title: "Thống kê bài đăng tuần trước",
    api: "fos_rpposts",
    showChart: true,
    chartLabel: "Ngày",
    generateChartLabels: (data) => {
      const clonedData = cloneDeep(data);
      clonedData.shift();
      return clonedData.map((item) => item.ngay);
    },
    generateChartValues: (data) => {
      const clonedData = cloneDeep(data);
      clonedData.shift();
      return clonedData.map((item) => item.sl);
    },
    convertData: (data) => {
      const result: { ngay: string; sl: number }[] = [];
      const groupedData: any[] = groupBy({
        data,
        callbackMatch: (item) => {
          const day = moment(item.date_created).format("DD/MM/YYYY");
          return day;
        },
      }) as any[];
      let total = 0;
      for (let i = 0; i < groupedData.length; i++) {
        const item = groupedData[i];
        result.push({
          ngay: formatDateDisplay(item[0].date_created),
          sl: item?.length || 0,
        });
        total += item.length || 0;
      }
      result.unshift({
        ngay: "Tổng cộng",
        sl: total,
      });
      return result;
    },
    columns: [
      {
        name: "Ngày",
        width: "130px",
        wrap: true,
        selector: (row) => row.ngay,
      },
      {
        name: "Số lượng",
        width: "150px",
        right: true,
        selector: (row) => row.sl,
      },
    ],
  },
  "7daysago": {
    label: "7daysago",
    value: "7daysago",
    title: "Thống kê bài đăng 7 ngày qua",
    api: "fos_rpposts",
    showChart: true,
    chartLabel: "Ngày",
    generateChartLabels: (data) => {
      const clonedData = cloneDeep(data);
      clonedData.shift();
      return clonedData.map((item) => item.ngay);
    },
    generateChartValues: (data) => {
      const clonedData = cloneDeep(data);
      clonedData.shift();
      return clonedData.map((item) => item.sl);
    },
    convertData: (data) => {
      const result: { ngay: string; sl: number }[] = [];
      const groupedData: any[] = groupBy({
        data,
        callbackMatch: (item) => {
          const day = moment(item.date_created).format("DD/MM/YYYY");
          return day;
        },
      }) as any[];
      let total = 0;
      for (let i = 0; i < groupedData.length; i++) {
        const item = groupedData[i];
        result.push({
          ngay: formatDateDisplay(item[0].date_created),
          sl: item?.length || 0,
        });
        total += item.length || 0;
      }
      result.unshift({
        ngay: "Tổng cộng",
        sl: total,
      });
      return result;
    },
    columns: [
      {
        name: "Ngày",
        width: "130px",
        wrap: true,
        selector: (row) => row.ngay,
      },
      {
        name: "Số lượng",
        width: "150px",
        right: true,
        selector: (row) => row.sl,
      },
    ],
  },
  thismonth: {
    label: "Thismonth",
    value: "thismonth",
    title: "Thống kê bài đăng tháng này",
    api: "fos_rpposts",
    showChart: true,
    chartLabel: "Ngày",
    generateChartLabels: (data) => {
      const clonedData = cloneDeep(data);
      clonedData.shift();
      return clonedData.map((item) => item.ngay);
    },
    generateChartValues: (data) => {
      const clonedData = cloneDeep(data);
      clonedData.shift();
      return clonedData.map((item) => item.sl);
    },
    convertData: (data) => {
      const result: { ngay: string; sl: number }[] = [];
      const groupedData: any[] = groupBy({
        data,
        callbackMatch: (item) => {
          const day = moment(item.date_created).format("DD/MM/YYYY");
          return day;
        },
      }) as any[];
      let total = 0;
      for (let i = 0; i < groupedData.length; i++) {
        const item = groupedData[i];
        result.push({
          ngay: formatDateDisplay(item[0].date_created),
          sl: item?.length || 0,
        });
        total += item.length || 0;
      }
      result.unshift({
        ngay: "Tổng cộng",
        sl: total,
      });
      return result;
    },
    columns: [
      {
        name: "Ngày",
        width: "130px",
        wrap: true,
        selector: (row) => row.ngay,
      },
      {
        name: "Số lượng",
        width: "150px",
        right: true,
        selector: (row) => row.sl,
      },
    ],
  },
  lastmonth: {
    label: "Lastmonth",
    value: "lastmonth",
    title: "Thống kê bài đăng tháng trước",
    api: "fos_rpposts",
    showChart: true,
    chartLabel: "Ngày",
    generateChartLabels: (data) => {
      const clonedData = cloneDeep(data);
      clonedData.shift();
      return clonedData.map((item) => item.ngay);
    },
    generateChartValues: (data) => {
      const clonedData = cloneDeep(data);
      clonedData.shift();
      return clonedData.map((item) => item.sl);
    },
    convertData: (data) => {
      const result: { ngay: string; sl: number }[] = [];
      const groupedData: any[] = groupBy({
        data,
        callbackMatch: (item) => {
          const day = moment(item.date_created).format("DD/MM/YYYY");
          return day;
        },
      }) as any[];
      let total = 0;
      for (let i = 0; i < groupedData.length; i++) {
        const item = groupedData[i];
        result.push({
          ngay: formatDateDisplay(item[0].date_created),
          sl: item?.length || 0,
        });
        total += item.length || 0;
      }
      result.unshift({
        ngay: "Tổng cộng",
        sl: total,
      });
      return result;
    },
    columns: [
      {
        name: "Ngày",
        width: "130px",
        wrap: true,
        selector: (row) => row.ngay,
      },
      {
        name: "Số lượng",
        width: "150px",
        right: true,
        selector: (row) => row.sl,
      },
    ],
  },
  "30daysago": {
    label: "30daysago",
    value: "30daysago",
    title: "Thống kê bài đăng 30 ngày qua",
    api: "fos_rpposts",
    showChart: true,
    chartLabel: "Ngày",
    generateChartLabels: (data) => {
      const clonedData = cloneDeep(data);
      clonedData.shift();
      return clonedData.map((item) => item.ngay);
    },
    generateChartValues: (data) => {
      const clonedData = cloneDeep(data);
      clonedData.shift();
      return clonedData.map((item) => item.sl);
    },
    convertData: (data) => {
      const result: { ngay: string; sl: number }[] = [];
      const groupedData: any[] = groupBy({
        data,
        callbackMatch: (item) => {
          const day = moment(item.date_created).format("DD/MM/YYYY");
          return day;
        },
      }) as any[];
      let total = 0;
      for (let i = 0; i < groupedData.length; i++) {
        const item = groupedData[i];
        result.push({
          ngay: formatDateDisplay(item[0].date_created),
          sl: item?.length || 0,
        });
        total += item.length || 0;
      }
      result.unshift({
        ngay: "Tổng cộng",
        sl: total,
      });
      return result;
    },
    columns: [
      {
        name: "Ngày",
        width: "130px",
        wrap: true,
        selector: (row) => row.ngay,
      },
      {
        name: "Số lượng",
        width: "150px",
        right: true,
        selector: (row) => row.sl,
      },
    ],
  },
  thisquarter: {
    value: "thisquarter",
    label: "thisquarter",
    title: "Thống kê bài đăng quý này",
    api: "fos_rpposts",
    showChart: true,
    chartLabel: "Thời gian",
    generateChartLabels: (data) => {
      const clonedData = cloneDeep(data);
      clonedData.shift();
      return clonedData.map((item) => item.thoigian);
    },
    generateChartValues: (data) => {
      const clonedData = cloneDeep(data);
      clonedData.shift();
      return clonedData.map((item) => item.sl);
    },
    convertData: (data) => {
      const result: { thoigian: string; sl: number }[] = [];
      const groupedData = groupBy({
        data,
        callbackMatch: (item) => {
          const month = moment(item.date_created).month();
          const year = moment(item.date_created).year();
          return `${month}/${year}`;
        },
      }) as any[];
      let total = 0;
      for (let i = 0; i < groupedData.length; i++) {
        const item = groupedData[i];
        const month = moment(item[0].date_created).month() + 1;
        const year = moment(item[0].date_created).year();
        result.push({
          thoigian: `Tháng ${month}/${year}`,
          sl: item?.length || 0,
        });
        total += item?.length || 0;
      }
      result.unshift({
        thoigian: "Tổng cộng",
        sl: total,
      });
      return result;
    },
    columns: [
      {
        name: "Thời gian",
        width: "150px",
        selector: (row) => row.thoigian,
      },
      {
        name: "Số lượng",
        width: "120px",
        right: true,
        selector: (row) => row.sl,
      },
    ],
  },
  lastquarter: {
    value: "lastquarter",
    label: "lastquarter",
    title: "Thống kê bài đăng quý trước",
    api: "fos_rpposts",
    showChart: true,
    chartLabel: "Thời gian",
    generateChartLabels: (data) => {
      const clonedData = cloneDeep(data);
      clonedData.shift();
      return clonedData.map((item) => item.thoigian);
    },
    generateChartValues: (data) => {
      const clonedData = cloneDeep(data);
      clonedData.shift();
      return clonedData.map((item) => item.sl);
    },
    convertData: (data) => {
      const result: { thoigian: string; sl: number }[] = [];
      const groupedData = groupBy({
        data,
        callbackMatch: (item) => {
          const month = moment(item.date_created).month();
          const year = moment(item.date_created).year();
          return `${month}/${year}`;
        },
      }) as any[];
      let total = 0;
      for (let i = 0; i < groupedData.length; i++) {
        const item = groupedData[i];
        const month = moment(item[0].date_created).month() + 1;
        const year = moment(item[0].date_created).year();
        result.push({
          thoigian: `Tháng ${month}/${year}`,
          sl: item?.length || 0,
        });
        total += item?.length || 0;
      }
      result.unshift({
        thoigian: "Tổng cộng",
        sl: total,
      });
      return result;
    },
    columns: [
      {
        name: "Thời gian",
        width: "150px",
        selector: (row) => row.thoigian,
      },
      {
        name: "Số lượng",
        width: "120px",
        right: true,
        selector: (row) => row.sl,
      },
    ],
  },
  thisyear: {
    value: "thisyear",
    label: "thisyear",
    title: "Thống kê bài đăng năm nay",
    api: "fos_rpposts",
    showChart: true,
    chartLabel: "Ngày",
    generateChartLabels: (data) => {
      const clonedData = cloneDeep(data);
      clonedData.shift();
      return clonedData.map((item) => item.thoigian);
    },
    generateChartValues: (data) => {
      const clonedData = cloneDeep(data);
      clonedData.shift();
      return clonedData.map((item) => item.sl);
    },
    convertData: (data) => {
      const result: { thoigian: string; sl: number }[] = [];
      const groupedData = groupBy({
        data,
        callbackMatch: (item) => {
          const quarter = moment(item.date_created).quarter();
          const year = moment(item.date_created).year();
          return `${quarter}/${year}`;
        },
      }) as any[];
      let total = 0;
      for (let i = 0; i < groupedData.length; i++) {
        const item = groupedData[i];
        const quarter = moment(item[0].date_created).quarter();
        const year = moment(item[0].date_created).year();
        result.push({
          thoigian: `Quý ${quarter}/${year}`,
          sl: item?.length || 0,
        });
        total += item?.length || 0;
      }
      result.unshift({
        thoigian: "Tổng cộng",
        sl: total,
      });
      return result;
    },
    columns: [
      {
        name: "Thời gian",
        width: "150px",
        selector: (row) => row.thoigian,
      },
      {
        name: "Số lượng",
        width: "120px",
        right: true,
        selector: (row) => row.sl,
      },
    ],
  },
  lastyear: {
    value: "thisyear",
    label: "thisyear",
    title: "Thống kê bài đăng năm trước",
    api: "fos_rpposts",
    showChart: true,
    chartLabel: "Thời gian",
    generateChartLabels: (data) => {
      const clonedData = cloneDeep(data);
      clonedData.shift();
      return clonedData.map((item) => item.thoigian);
    },
    generateChartValues: (data) => {
      const clonedData = cloneDeep(data);
      clonedData.shift();
      return clonedData.map((item) => item.sl);
    },
    convertData: (data) => {
      const result: { thoigian: string; sl: number }[] = [];
      const groupedData = groupBy({
        data,
        callbackMatch: (item) => {
          const quarter = moment(item.date_created).quarter();
          const year = moment(item.date_created).year();
          return `${quarter}/${year}`;
        },
      }) as any[];
      let total = 0;
      for (let i = 0; i < groupedData.length; i++) {
        const item = groupedData[i];
        const quarter = moment(item[0].date_created).quarter();
        const year = moment(item[0].date_created).year();
        result.push({
          thoigian: `Quý ${quarter}/${year}`,
          sl: item?.length || 0,
        });
        total += item?.length || 0;
      }
      result.unshift({
        thoigian: "Tổng cộng",
        sl: total,
      });
      return result;
    },
    columns: [
      {
        name: "Thời gian",
        width: "150px",
        selector: (row) => row.thoigian,
      },
      {
        name: "Số lượng",
        width: "120px",
        right: true,
        selector: (row) => row.sl,
      },
    ],
  },
};

function FilterRPTPost({ setQueryObject, setConcern }: IFilterReport) {
  const originFilter = {
    type: "feed",
    timeFrom: moment().startOf("months").format("YYYY-MM-DD"),
    timeTo: moment().endOf("days").format("YYYY-MM-DD"),
  };
  const [filter, setFilter] = useState<{ [key: string]: any }>(originFilter);
  const [valueConcern, setValueConcern] = useState(concerns[0].value);
  const [timeOption, setTimeOption] = useState<any>();

  const handleRefreshFilter = () => {
    setFilter(originFilter);
  };

  useEffect(() => {
    const queryObject: { [key: string]: any } = {};
    if (filter.type) {
      queryObject.type = filter.type;
    }
    if (filter.timeFrom) {
      queryObject.tu_ngay = moment(filter.timeFrom)
        .startOf("days")
        .toISOString();
    }
    if (filter.timeTo) {
      queryObject.den_ngay = moment(filter.timeTo).endOf("days").toISOString();
    }
    setQueryObject(queryObject);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  useEffect(() => {
    if (valueConcern !== "thoigian") {
      setConcern(concerns.find((item) => item.value === valueConcern));
    } else {
      let currentTimeConcern;
      if (timeOption?.value) {
        currentTimeConcern = timeConcerns[timeOption.value as TimeType];
      } else {
        currentTimeConcern = timeConcerns.fromTo;
      }
      setConcern(currentTimeConcern);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [valueConcern, timeOption?.value]);

  return (
    <FilterContainer handleRefreshFilter={handleRefreshFilter}>
      <Stack sx={{ width: "100%" }} gap={1}>
        <FilterRadios
          title="Thống kê theo"
          values={concerns}
          defaultValue={valueConcern}
          onChange={(newConcern) => setValueConcern(newConcern)}
        />
        <FilterRadios
          title="Loại bài viết"
          values={types}
          defaultValue={filter.type}
          onChange={(val) => setFilter((prev) => ({ ...prev, type: val }))}
        />
        <FilterTimeFromTo
          showOptions
          defaultTimeFrom={filter.timeFrom}
          defaultTimeTo={filter.timeTo}
          title="Thời gian"
          onOptionChange={setTimeOption}
          onSearch={(time) => setFilter({ ...filter, ...time })}
        />
      </Stack>
    </FilterContainer>
  );
}

export default FilterRPTPost;
